// src/pages/invoices/components/CustomerDropdown.tsx
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Customer } from '@/types/customer.types';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

interface CustomerDropdownProps {
  customers: Customer[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
}

const CustomerDropdown = ({
  customers,
  value,
  onChange,
  placeholder = 'Select customer...',
  error,
}: CustomerDropdownProps) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const navigate = useNavigate();

  const selectedCustomer = customers.find((c) => c._id === value);

  const getDisplayName = (customer: Customer) => {
    if (customer.customerType === 'business' && customer.companyName) {
      return `${customer.primaryContact} - ${customer.companyName}`;
    }
    return customer.primaryContact || '';
  };

  const filteredCustomers = customers.filter((customer) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      customer.primaryContact?.toLowerCase().includes(searchLower) ||
      customer.companyName?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  const handleSelect = (customer: Customer) => {
    onChange(customer._id);
    setOpen(false);
    setSearch('');
  };

  const handleAddNew = () => {
    setOpen(false);
    navigate('/customers/new');
  };

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          type="button"
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
            'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'hover:bg-accent hover:text-accent-foreground',
            error && 'border-red-500 focus:ring-red-500'
          )}
        >
          {selectedCustomer ? (
            <span className="truncate text-left flex-1">{getDisplayName(selectedCustomer)}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </PopoverTrigger>

        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="p-2 border-b">
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer..."
              className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>

          <div className="max-h-60 overflow-auto p-1">
            {filteredCustomers.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No customer found.
              </div>
            ) : (
              filteredCustomers.map((customer) => (
                <div
                  key={customer._id}
                  onClick={() => handleSelect(customer)}
                  className={cn(
                    'flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                    value === customer._id && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === customer._id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{getDisplayName(customer)}</span>
                    <span className="text-xs text-muted-foreground">
                      {customer.email || 'No email'} • {customer.currency || 'INR'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t p-1">
            <div
              onClick={handleAddNew}
              className="flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm text-primary hover:bg-accent"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Customer
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default CustomerDropdown;
